import React, { useEffect, useRef, useState} from "react";
import * as d3 from "d3";
import DounutSkeleton from "./DounutSkeleton";
import { roundDecimalValueUptoTwo } from "utils/roundDecimalValue";
import { Typography } from "@mui/material";
import { formatFixedOneNotation } from "components/Reports/DashboardComponents/Home/StoreListCards/data";
import { calculateGrowth } from "components/Reports/data";


// --- Helper to format numbers ---
const formatNumber = (num) => {
	if (num >= 1e7) return roundDecimalValueUptoTwo(num / 1e7) + " Cr";
	if (num >= 1e5) return roundDecimalValueUptoTwo(num / 1e5).toFixed(2) + " L";
	if (num >= 1e3) return roundDecimalValueUptoTwo(num / 1e3).toFixed(1) + " K";
	return roundDecimalValueUptoTwo(num);
};

const defColors=["#6B7A8F", "#D9C7B4", "#B4CDE6", "#E8B4B8", "#C9A3A0"]

const DounutChart = ({chartName = "",chart_id, data, width = 300, height = 300, colors, setSelectedArc, selectedArc }) => {
	const ref = useRef();
  const [chartVersion, setChartVersion] = useState(0);

// console.log("selectedArcratyud", selectedArc)
	useEffect(() => {
    if (!data || data.length === 0) return;

    const svgElement = d3.select(ref.current);
    svgElement.selectAll("*").remove();  // clear old chart

    data.forEach((d, i) => {
      d.color = colors[i % colors.length];
    });

    setChartVersion(v => v + 1);

    const svg = svgElement
    .attr("width", width)
    .attr("height", height)
    .append("g")
    .attr("transform", `translate(${width / 2}, ${height / 2})`);

    const radius = Math.min(width, height) / 2;
    const pie = d3.pie().sort(null).value((d) => d.qty);
    const arcs = pie(data);
    const totalValue = d3.sum(data, d => d.qty);
    const ly_totalValue = d3.sum(data, d => d.ly_qty);

    const growth = calculateGrowth(ly_totalValue, totalValue)
    
      const growthValue = parseFloat(growth).toFixed(1); // ensure it's a number
      const growthColor = growthValue >= 0 ? "green" : "red";

    const arc = d3.arc().innerRadius(radius * 0.5).outerRadius(radius * 0.9);

    const onClickArc = (event, d) => {
    const clickedLabel = d?.data?.label;
    if (!clickedLabel) return;

    // If same arc clicked again → deselect
    if (selectedArc && selectedArc.label === clickedLabel && chartName === selectedArc.chartName) {
      svg.selectAll("path.arc")
        .transition()
        .duration(200)
        .style("opacity", 1)
        .style("transform", "scale(1)")
        .attr("stroke", "#fff");

      setSelectedArc(null);
      return;
    }

    // Dim others
    svg.selectAll("path.arc")
      .transition()
      .duration(200)
      .style("opacity", 0.3)
      .style("transform", "scale(1)")
      .attr("stroke", "#fff");

    // Highlight selected
    d3.select(event.currentTarget) // use event.currentTarget instead of this in React
      .transition()
      .duration(200)
      .style("opacity", 1)
      .style("transform", "scale(1.08)")
      .attr("stroke", "#000");

    setSelectedArc({ ...d.data, chartName, chart_id });
  };


	// Create tooltip first
	const tooltip = d3
	.select("body")
	.append("div")
	.attr("class", "venntooltip")
	.style("position", "absolute")
	// .style("text-align", "center")
	.style("padding", "6px 10px")
	.style("font-size", "var(--font-size-tooltip)")
	.style("background", "var( --color-tooltip-background)")
	.style("color", "var(--color-tooltip)")
	.style("border-radius", "6px")
	.style("pointer-events", "none")
	.style("opacity", 0);

    // Donut segments
    svg
		.selectAll("path.arc")
		.data(arcs)
		.join("path")
    .attr("class", "arc")
		.attr("d", arc)
		.attr("fill", (d) => d.data.color)
		.attr("stroke", "#fff")
		.style("stroke-width", "2px")
    .style("cursor", "pointer")
    .style("transition", "all 0.2s ease")
		.on("mouseover", function (event, d) {
			d3.select(this)
			.style("transform", "scale(1.05)")
			.style("transition", "0.15s");

      const percentage = ((d.data.qty / totalValue) * 100).toFixed(1);
      const arcGrowth = calculateGrowth(d.data.ly_qty, d.data.qty)
    
      const qtyGrowth = parseFloat(arcGrowth).toFixed(1); // ensure it's a number
      const growthColor = qtyGrowth >= 0 ? "green" : "red";

			tooltip
			.style("opacity", 1)
			.html(`<span>${d.data.label} <b>(${percentage}%)</b></span><br/>TY: ${formatFixedOneNotation(d.data.qty, "qty")} <span style="color:${growthColor}; font-weight:bold;">(${qtyGrowth}%)</span><br/>LY: ${d.data.ly_qty ? formatFixedOneNotation(d.data.ly_qty, "qty") : 0}`)
			.style("left", event.pageX + 10 + "px")
			.style("top", event.pageY - 30 + "px");
		})
		.on("mousemove", function (event) {
			tooltip
			.style("left", event.pageX + 10 + "px")
			.style("top", event.pageY - 30 + "px");
		})
		.on("mouseout", function () {
			d3.select(this)
			.style("transform", "scale(1)")
			.style("transition", "0.15s");
			tooltip.style("opacity", 0);
		})
    .on("click", function (event, d) {
				onClickArc(event, d);
			});

    // Arc paths for curved text

	const labelArc = d3.arc()
  .innerRadius(radius * 0.75)
  .outerRadius(radius * 0.75);


  // Define arc paths
// const defs = svg.append("defs");
// defs.selectAll("path")
//   .data(arcs)
//   .join("path")
//   .attr("id", (d, i) => `arcLabel${i}`)
//   .attr("fill", "none")
//   .attr("d", (d) => {
//         const midAngle = (d.startAngle + d.endAngle) / 2;
//         if (midAngle > Math.PI) {
//           // flip text path on bottom half
//           const reversed = { ...d, startAngle: d.endAngle, endAngle: d.startAngle };
//           return labelArc(reversed);
//         }
//         return labelArc(d);
//       });
  
//     svg
//       .selectAll("defs")
//       .data(arcs)
//       .join("defs")
//       .append("path")
//       .attr("id", (d, i) => `arcLabel${i}`)
//       .attr(
//         "d",
//         d3
//           .arc()
//           .innerRadius(radius * 0.75)
//           .outerRadius(radius * 0.75)
//       );

//     // Add text along the arcs
//     svg
//   .selectAll("text.arc-text")
//   .data(arcs)
//   .join("text")
//   .attr("class", "arc-text")
//   .append("textPath")
//   .attr("xlink:href", (d, i) => `#arcLabel${i}`)
//   .attr("startOffset", "10%")
//   .style("text-anchor", "middle")
//   .style("font-size", "12px")
//   .style("cursor", "pointer")
//   .style("fill", "#333")
//   .text((d) => {
//     const angle = d.endAngle - d.startAngle;
//     const minAngle = 0.35; // minimum arc size to fit text
//     if (angle <= minAngle) {
//       return ""; // hide text for very small arcs
//     }
//     else if (angle <= 0.6) {
//       // truncate text for small arcs
//       const truncatedLabel =d.data.label.slice(0, 6);
//       return `${truncatedLabel}`;
//     }
//     const percentage = ((d.data.qty / totalValue) * 100).toFixed(1) + "%";
//     return `${d.data.label} ${percentage}`;
//   })
//   .each(function (d) {
//     const textNode = d3.select(this);
//     const bbox = this.getBBox();
//     const arcLength = (d.endAngle - d.startAngle) * radius * 0.75;

//     // Case 1: Arc too small to display text at all
//     if ((d.endAngle - d.startAngle) < 0.35) {
//       textNode.text(""); // Hide text
//       textNode.append("title").text(d.data.label); // Tooltip only
//       return;
//     }

//     // Case 2: Label too long — truncate with tooltip

//     if (bbox.width + 30 > arcLength) {
//       const truncated = d.data.label.slice(0, 10) + "…";
//       textNode.text(truncated);

//       textNode.append("title").text(d.data.label);
//     } else {
//       // Case 3: Fits fine — add tooltip anyway for hover clarity
//       textNode.append("title").text(d.data.label);
//     }
//   })
  // .on("click", function (event, d) {
	// 			onClickArc(event, d);
	// 		});

    // Center content
    // svg
    //   .append("text")
    //   .attr("text-anchor", "middle")
    //   .attr("y", -30)
    //   .style("font-size", "12px")
    //   .style("fill", "#555")
    //   .text("CUSTOMER (K)");

    svg
      .append("text")
      .attr("text-anchor", "middle")
      .attr("y", -10)
      .style("font-size", "var(--font-size-h1)")
      .style("font-weight", "bold")
      .text(formatFixedOneNotation(totalValue, "qty"));

    svg
      .append("text")
      .attr("text-anchor", "middle")
      .attr("y", 15)
      .style("font-size", "var(--font-size-body3)")
      .html(`VS ${formatFixedOneNotation(ly_totalValue, "qty")} `)
      .append("tspan")
      .style("fill", growthColor)
      .text(`(${growthValue}%)`);
    // svg
    //   .append("text")
    //   .attr("text-anchor", "middle")
    //   .attr("y", 30)
    //   .style("font-size", "12px")
    //   .append("tspan")
    //   .text(" 100% | 10%");

      return () => {
    tooltip.remove();
  };
  }, [data, width, height, colors, setSelectedArc,selectedArc]);

  // --- React to external selection updates ---
  useEffect(() => {
    const svg = d3.select(ref.current).select("g");
    if (svg.empty()) return;
    if (selectedArc && chartName === selectedArc.chartName) {
      svg
        .selectAll("path.arc")
        .transition()
        .duration(200)
        .style("opacity", (d) =>
          d.data.label === selectedArc.label ? 1 : 0.3
        )
        .attr("stroke", (d) =>
          d.data.label === selectedArc.label ? "#000" : "#fff"
        )
        .style("transform", (d) =>
          d.data.label === selectedArc.label ? "scale(1.08)" : "scale(1)"
        );
    } else {
      svg
        .selectAll("path.arc")
        .transition()
        .duration(200)
        .style("opacity", 1)
        .attr("stroke", "#fff")
        .style("transform", "scale(1)");
    }
  }, [selectedArc, chartVersion]);

	// if (loading) return ;
	// else
		return (
    <div style={{ textAlign: "center", fontFamily: "sans-serif", width: width, height: height }}>
      <Typography sx={{fontSize: "var(--font-size-h3)"}} style={{ width:width }}>{chartName}</Typography>
      {data.length === 0? <DounutSkeleton width={width} height={height} /> : <svg ref={ref}></svg>}
    </div>
  );
}
export default DounutChart;
